import { useEffect, useRef, type RefObject } from 'react';

/**
 * Back to the top of the page whenever `value` changes — not when the component first mounts.
 *
 * MYP-118: the order steps swap their content in place, and the whole page (the window, not an
 * inner box) scrolls, so the browser kept the old position: pressing Continue at the bottom of
 * Step 1 opened Step 2 already scrolled to its bottom. Instant rather than smooth, the same as the
 * Notifications pager — quicker, and nothing to switch off for people who turn animations down.
 *
 * `focusRef`, when given, then takes keyboard focus (without scrolling again), so keyboard and
 * screen-reader users start at the top of the new content rather than on a button that has gone.
 *
 * Compared with the previous value rather than skipping the first run, so React's development
 * double-run of effects doesn't count as a change.
 */
export function useScrollToTopOnChange(
  value: unknown,
  focusRef?: RefObject<HTMLElement | null>,
): void {
  const previous = useRef(value);

  useEffect(() => {
    if (Object.is(previous.current, value)) return;
    previous.current = value;
    window.scrollTo({ top: 0 });
    focusRef?.current?.focus({ preventScroll: true });
  }, [value, focusRef]);
}
